import {Pattern, match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {equals, isNotNil} from 'ramda';

import {aiAssistantContextStore} from '@dms/ai-assistant';
import {getTeasServerNow} from '@dms/api/base';

import type {AssistantBusinessCaseContext} from '@prompt-bridge/shared/ai-assistant-core';

import {useLatest} from 'shared/web';

import {ASSISTANT_BUSINESS_CASE_CONTEXT_REFRESH_INTERVAL_MS} from '../constants/ASSISTANT_BUSINESS_CASE_CONTEXT_REFRESH_INTERVAL_MS';
import {assistantBusinessCaseContextLoadingStore} from '../store/assistantBusinessCaseContextLoadingStore';

type ContextOwnerToken = object;
type CreateAssistantBusinessCaseContext = (
  evaluatedAt: string
) => AssistantBusinessCaseContext | undefined;
type SynchronizationDependency = boolean | object | string | undefined;
type PublishLatestOptions = {
  createContext?: CreateAssistantBusinessCaseContext;
  force?: boolean;
};
type RefreshBusinessCaseContext = () => Promise<unknown>;
type SynchronizeRefState = {
  dependencies: readonly SynchronizationDependency[];
  synchronizeRef: (node: HTMLSpanElement | null) => void;
};

let activeBusinessCaseContextOwnerToken: ContextOwnerToken | undefined;

const toRenderComparableBusinessCaseContext = (
  businessCase: AssistantBusinessCaseContext | undefined
) =>
  match(businessCase)
    .with(Pattern.nonNullable, (availableBusinessCase) => ({
      ...availableBusinessCase,
      evaluatedAt: undefined,
      signals: availableBusinessCase.signals.map((signal) => ({...signal, evaluatedAt: undefined})),
    }))
    .otherwise(() => undefined);

const areRenderBusinessCaseContextsEqual = (
  left: AssistantBusinessCaseContext | undefined,
  right: AssistantBusinessCaseContext | undefined
) =>
  equals(toRenderComparableBusinessCaseContext(left), toRenderComparableBusinessCaseContext(right));

const areSynchronizationDependenciesEqual = (
  left: readonly SynchronizationDependency[],
  right: readonly SynchronizationDependency[]
) =>
  left.length === right.length &&
  left.every((dependency, index) => Object.is(dependency, right[index]));

export function usePublishAssistantBusinessCaseContext(
  createBusinessCaseContext: CreateAssistantBusinessCaseContext,
  synchronizationDependencies: readonly SynchronizationDependency[],
  isLoading = false,
  refreshBusinessCaseContext?: RefreshBusinessCaseContext
) {
  const latestCreateBusinessCaseContextRef = useLatest(createBusinessCaseContext);
  const latestIsLoadingRef = useLatest(isLoading);
  const latestRefreshBusinessCaseContextRef = useLatest(refreshBusinessCaseContext);
  const lastPublishedRef = useRef<AssistantBusinessCaseContext | undefined>(undefined);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const unregisterSendSynchronizerRef = useRef<(() => void) | undefined>(undefined);
  const [ownerToken] = useState<ContextOwnerToken>(() => ({}));
  const [publishLatest] = useState(
    () =>
      ({
        createContext = latestCreateBusinessCaseContextRef.current,
        force = false,
      }: PublishLatestOptions = {}) => {
        const latestBusinessCase = createContext(getTeasServerNow() ?? new Date().toISOString());

        const ownsPublishedContext = activeBusinessCaseContextOwnerToken === ownerToken;

        if (
          !force &&
          areRenderBusinessCaseContextsEqual(lastPublishedRef.current, latestBusinessCase) &&
          ownsPublishedContext
        ) {
          return;
        }

        lastPublishedRef.current = latestBusinessCase;
        match({
          latestBusinessCase,
          ownsPublishedContext,
        })
          .with({latestBusinessCase: Pattern.nullish, ownsPublishedContext: false}, () => undefined)
          .with({latestBusinessCase: Pattern.nullish, ownsPublishedContext: true}, () => {
            activeBusinessCaseContextOwnerToken = undefined;
            aiAssistantContextStore.patchContext({businessCase: undefined});
          })
          .with(
            {latestBusinessCase: Pattern.nonNullable},
            ({latestBusinessCase: nextBusinessCase}) => {
              activeBusinessCaseContextOwnerToken = ownerToken;
              aiAssistantContextStore.patchContext({businessCase: nextBusinessCase});
            }
          )
          .exhaustive();
      }
  );
  const [ownerRef] = useState(() => (node: HTMLDivElement | null) => {
    if (isNotNil(node)) {
      assistantBusinessCaseContextLoadingStore.setLoading(latestIsLoadingRef.current);
      publishLatest();
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = setInterval(() => {
        const refreshBusinessCaseContext = latestRefreshBusinessCaseContextRef.current;
        if (isNotNil(refreshBusinessCaseContext)) {
          const publishIfStillActive = () => {
            if (isNotNil(refreshIntervalRef.current)) {
              publishLatest();
            }
          };
          void refreshBusinessCaseContext().then(publishIfStillActive, publishIfStillActive);
          return;
        }

        publishLatest();
      }, ASSISTANT_BUSINESS_CASE_CONTEXT_REFRESH_INTERVAL_MS);
      unregisterSendSynchronizerRef.current?.();
      unregisterSendSynchronizerRef.current = aiAssistantContextStore.registerSendSynchronizer(() =>
        publishLatest({force: true})
      );
      return;
    }

    clearInterval(refreshIntervalRef.current);
    refreshIntervalRef.current = undefined;
    unregisterSendSynchronizerRef.current?.();
    unregisterSendSynchronizerRef.current = undefined;
    assistantBusinessCaseContextLoadingStore.setLoading(false);

    if (activeBusinessCaseContextOwnerToken === ownerToken) {
      activeBusinessCaseContextOwnerToken = undefined;
      lastPublishedRef.current = undefined;
      aiAssistantContextStore.patchContext({businessCase: undefined});
    }
  });
  const synchronizationStateDependencies = [...synchronizationDependencies, isLoading];
  const createSynchronizeRefState = (): SynchronizeRefState => ({
    dependencies: synchronizationStateDependencies,
    synchronizeRef: (node) => {
      if (isNotNil(node)) {
        assistantBusinessCaseContextLoadingStore.setLoading(isLoading);
        publishLatest({createContext: createBusinessCaseContext});
      }
    },
  });
  const [initialSynchronizeRefState] = useState(createSynchronizeRefState);
  const synchronizeRefStateRef = useRef<SynchronizeRefState>(initialSynchronizeRefState);

  if (
    !areSynchronizationDependenciesEqual(
      synchronizeRefStateRef.current.dependencies,
      synchronizationStateDependencies
    )
  ) {
    synchronizeRefStateRef.current = createSynchronizeRefState();
  }

  return {ownerRef, synchronizeRef: synchronizeRefStateRef.current.synchronizeRef};
}
